﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ALL_CUSTOM_VIDEO_THEME_QUERY_KEY,
  CUSTOM_VIDEO_THEME_API_URL,
  CUSTOM_VIDEO_THEME_BY_ID_QUERY_KEY
} from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import { ErrorResponse, Options } from '@/types';
import { CustomVideoThemeCreate, CustomVideoThemeEdit } from '@/types/CustomVideoThemeTypes';

export const customVideoThemeEndpoints = {
  getAllCustomVideoTheme: (
    pageSize: number,
    cursor: number,
    eventId?: number,
    searchQuery?: string,
    sortField?: string,
    sortingAsc?: boolean
  ) =>
    axios.get(
      `${apiUrl()}${CUSTOM_VIDEO_THEME_API_URL}${getQueryParams({
        'paging.PageSize': pageSize,
        'paging.Cursor': cursor,
        'filter.EventId': eventId as number,
        'sorting.Field': sortField as string,
        'sorting.Asc': sortingAsc as boolean,
        'search.Text': searchQuery as string
      })}`
    ),
  createCustomVideoTheme: (payload: CustomVideoThemeCreate) =>
    axios.post(`${apiUrl()}${CUSTOM_VIDEO_THEME_API_URL}`, payload),
  updateCustomVideoTheme: (payload: CustomVideoThemeEdit) =>
    axios.put(`${apiUrl()}${CUSTOM_VIDEO_THEME_API_URL}`, payload),
  getCustomVideoTheme: (id: number) => axios.get(`${apiUrl()}${CUSTOM_VIDEO_THEME_API_URL}/${id}`)
};

export const useGetAllCustomVideoTheme = (
  pageSize: number,
  eventId?: number,
  sortField?: string,
  sortingAsc?: boolean,
  searchQuery?: string
) => {
  return useInfiniteQuery(
    [ALL_CUSTOM_VIDEO_THEME_QUERY_KEY, { pageSize, eventId, searchQuery, sortField, sortingAsc }],
    ({ pageParam }) =>
      customVideoThemeEndpoints.getAllCustomVideoTheme(
        pageSize,
        pageParam,
        eventId,
        searchQuery,
        sortField,
        sortingAsc
      ),
    {
      keepPreviousData: true,
      enabled: true,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useCreateCustomVideoThemeMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(customVideoThemeEndpoints.createCustomVideoTheme, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([ALL_CUSTOM_VIDEO_THEME_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateCustomVideoThemeMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(customVideoThemeEndpoints.updateCustomVideoTheme, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([ALL_CUSTOM_VIDEO_THEME_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetCustomVideoThemeById = (id: number, options?: Options) => {
  return useQuery(
    [CUSTOM_VIDEO_THEME_BY_ID_QUERY_KEY, { id }],
    () => customVideoThemeEndpoints.getCustomVideoTheme(id),
    {
      enabled: !!id,
      retry: false,
      onSuccess: () => {
        options?.onSuccess();
      },
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};
